// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Clatter — the opponent. Principle #1 says the game must be fun alone in the first five seconds,
// which for a score race means the bot has to actually play the game rather than grab the biggest
// number. It is a one-ply evaluator with a shaped heuristic, and it is deliberately CHEAP: the
// legal set is at most a couple of dozen actions, each evaluation is one small sheet clone plus a
// cascade, so a whole 24-turn four-seat game costs well under a millisecond. That budget is what
// lets tests/balance.test.ts play hundreds of games inside the default `npm test`.
//
// It is also DETERMINISTIC given (state, seat, tier, seed). That is load-bearing twice over: the
// balance sim needs it to be reproducible, and the netcode needs it because a seat that goes quiet
// is filled in by whichever peer notices first — two peers must produce the byte-identical action.

import {
  DIE_NONE,
  PH_ACTIVE,
  RUN_FLOOR,
  SHIM_NONE,
  T_LADDER,
  T_NONE,
  T_RUN,
  T_TALLY,
  T_WEIGHTS,
  WEIGHT_PAIRS,
  aDie,
  aShim,
  aSlot,
  aTrack,
  activeSeat,
  cloneSheet,
  isPending,
  ladderFilled,
  legalActions,
  place,
  score,
  shimDelta,
  splitPool,
  type Sheet,
  type State,
} from './rules';
import { makeRng } from '@ben-gy/game-engine/rng';

export interface Weights {
  /** Value of a cell filled, over and above the points it scores. Progress is its own reward. */
  cell: number;
  /** Ladder: leaving headroom above the face you wrote. */
  ladderHead: number;
  /** Run: burning a 6 on a floor-1 cell is a waste of a scarce face. */
  runWaste: number;
  /** Weights: opening a pair whose complement is a common face. */
  pairOpen: number;
  /** What a shim is worth if you keep it rather than spend it. */
  shimHold: number;
  /** How much the active player cares what the spill is worth to everybody else. */
  generosity: number;
  /** Random jitter, in points. The only source of tier-to-tier weakness. */
  noise: number;
}

export interface Tier {
  id: string;
  name: string;
  weights: Weights;
}

const SHARP: Weights = {
  cell: 0.85,
  ladderHead: 0.4,
  runWaste: 0.3,
  pairOpen: 1.1,
  shimHold: 1.7,
  generosity: 0.85,
  noise: 0,
};

export const TIERS: Record<string, Tier> = {
  novice: {
    id: 'novice',
    name: 'Novice',
    // Blind to the give-away and noisy: it plays its own sheet and never thinks about yours.
    weights: { ...SHARP, generosity: 0, pairOpen: 0, ladderHead: 0, noise: 4.5 },
  },
  steady: {
    id: 'steady',
    name: 'Steady',
    weights: { ...SHARP, generosity: 0.45, noise: 1.6 },
  },
  sharp: {
    id: 'sharp',
    name: 'Sharp',
    weights: SHARP,
  },
};

export const TIER_IDS = ['novice', 'steady', 'sharp'];
export const DEFAULT_TIER = 'steady';

export function tierOf(id: string | null | undefined): Tier {
  if (typeof id === 'string' && Object.hasOwn(TIERS, id)) return TIERS[id];
  return TIERS[DEFAULT_TIER];
}

/**
 * How useful a face is to a sheet, in rough points, without running the cascade. Used only to price
 * what the spill hands the other players — it has to be cheap because it runs once per rival per
 * spilled face.
 */
export function quickValue(sheet: Sheet, face: number): number {
  let best = 0;
  const k = ladderFilled(sheet);
  if (k < 5 && (k === 0 || face > sheet.ladder[k - 1])) best = Math.max(best, 2.2);
  if (sheet.tally[face - 1] < 2) best = Math.max(best, sheet.tally[face - 1] === 1 ? 3.4 : 1.8);
  if (sheet.run < 7 && face >= RUN_FLOOR[sheet.run]) best = Math.max(best, 2.6);
  for (let p = 0; p < WEIGHT_PAIRS; p++) {
    const w = sheet.weights[p];
    if (w === 0) best = Math.max(best, 1.4);
    else if (w !== 10 && face === 7 - w) best = Math.max(best, 4.2);
  }
  return best;
}

/** Shaped value of a single placement, on top of the points it actually scores. */
function shape(before: Sheet, track: number, face: number, slot: number, w: Weights): number {
  let v = w.cell;
  if (track === T_LADDER) {
    v += (6 - face) * w.ladderHead;
  } else if (track === T_RUN) {
    v -= (face - RUN_FLOOR[before.run]) * w.runWaste;
  } else if (track === T_WEIGHTS) {
    if (before.weights[slot] === 0) {
      // A pair opened on a 3 or a 4 needs a 4 or a 3 — the commonest faces on the table. A pair
      // opened on a 6 needs a literal 1, which is the die everybody else is also hunting.
      const complement = 7 - face;
      v += (2.5 - Math.abs(3.5 - complement)) * w.pairOpen;
    } else {
      v += w.pairOpen;
    }
  } else if (track === T_TALLY) {
    // Depth is worth more than breadth here only because the column bonus lives on the top cell.
    v += before.tally[face - 1] === 1 ? 0.5 : 0;
  }
  return v;
}

/** The evaluator. Deterministic; `jitter` is a pure function of the action, never Math.random. */
export function evaluate(s: State, seat: number, action: number, w: Weights, jitterSeed: number): number {
  const sheet = s.sheets[seat];
  const before = score(sheet, s.mode).total;
  const die = aDie(action);
  const track = aTrack(action);
  const slot = aSlot(action);
  const shim = aShim(action);

  const after = cloneSheet(sheet);
  let v = 0;

  if (s.phase === PH_ACTIVE) {
    const { spill } = splitPool(s.pool, die);
    if (track === T_NONE) {
      after.passed++;
      if (after.shims < 3) after.shims++;
      else after.pts += 2;
    } else {
      const face = s.pool[die] + shimDelta(shim);
      v += shape(after, track, face, slot, w);
      place(after, seat, track, face, slot, []);
      if (shim !== SHIM_NONE) after.shims--;
    }
    // What the table gets. Priced from each rival's OWN sheet, because a 4 is a gift to a player
    // holding an open 3 and worthless to a player whose tally column 4 is already full.
    if (spill.length > 0 && (w.generosity > 0 || s.mode.tribute)) {
      // MAX over rivals, not the sum. A race is run against whoever is closest to winning, so
      // handing four points to each of three rivals is not twelve points of damage — it is four,
      // spread. Summing it made the give-away cost scale with the player count while the reward did
      // not, and the bots duly starved the table every single turn: at four players the spill was
      // empty almost always and every sheet finished with seven of thirty-two cells filled.
      let rivalGain = 0;
      let takers = 0;
      for (let i = 0; i < s.n; i++) {
        if (i === seat) continue;
        let best = 0;
        for (const f of spill) best = Math.max(best, quickValue(s.sheets[i], f));
        rivalGain = Math.max(rivalGain, best);
        // A rival with a legal home for something in the spill is a rival who must take it — and
        // in Tribute that is a point in your pocket.
        if (best > 0) takers++;
      }
      v -= rivalGain * w.generosity;
      if (s.mode.tribute) v += takers;
    }
  } else if (die === DIE_NONE || track === T_NONE) {
    after.passed++;
    if (after.shims < 3) after.shims++;
    else after.pts += 2;
  } else {
    const face = s.spill[die] + shimDelta(shim);
    v += shape(after, track, face, slot, w);
    place(after, seat, track, face, slot, []);
    if (shim !== SHIM_NONE) after.shims--;
  }

  if (shim !== SHIM_NONE) v -= w.shimHold;
  v += score(after, s.mode).total - before;

  if (w.noise > 0) {
    // Deterministic jitter: a pure hash of (seed, action), so a replay and a fill-in agree.
    const r = makeRng((jitterSeed ^ (action >>> 0)) >>> 0);
    v += (r() - 0.5) * 2 * w.noise;
  }
  return v;
}

/**
 * The move. Ties break on the lowest packed action so two peers filling in for the same absent
 * seat produce byte-identical bytes — which is what makes the fill-in safe in lockstep.
 */
export function choose(s: State, seat: number, tier: Tier, seed: number): number {
  if (!isPending(s, seat)) return -1;
  const acts = legalActions(s, seat);
  if (acts.length === 0) return -1;
  const jitter = ((seed >>> 0) ^ ((s.turn + 1) * 0x85ebca6b) ^ (seat * 0x27d4eb2f)) >>> 0;
  let best = acts[0];
  let bestV = -Infinity;
  for (const a of acts) {
    const v = evaluate(s, seat, a, tier.weights, jitter);
    if (v > bestV + 1e-9 || (Math.abs(v - bestV) <= 1e-9 && a < best)) {
      best = a;
      bestV = v;
    }
  }
  return best;
}

/**
 * The control arm for the balance sim (principle #18): a legal player with no strategy at all. It
 * takes the first legal action it is offered, which after the canonical ordering of `legalActions`
 * means the highest die into the leftmost track. It must stay this dumb — a control that quietly
 * gains the treatment's competence proves nothing.
 */
export function chooseGreedy(s: State, seat: number): number {
  const acts = legalActions(s, seat);
  if (acts.length === 0) return -1;
  // Prefer the biggest immediate score, nothing else. No give-away term, no shaping, no lookahead.
  let best = acts[0];
  let bestV = -Infinity;
  const before = score(s.sheets[seat], s.mode).total;
  for (const a of acts) {
    const after = cloneSheet(s.sheets[seat]);
    const track = aTrack(a);
    if (track !== T_NONE) {
      const dice = s.phase === PH_ACTIVE ? s.pool : s.spill;
      const face = dice[aDie(a)] + shimDelta(aShim(a));
      place(after, seat, track, face, aSlot(a), []);
      if (aShim(a) !== SHIM_NONE) after.shims--;
    }
    const v = score(after, s.mode).total - before;
    if (v > bestV + 1e-9 || (Math.abs(v - bestV) <= 1e-9 && a < best)) {
      best = a;
      bestV = v;
    }
  }
  return best;
}

/** Which seats still owe a decision, for a caller that drives every seat (solo and the sim). */
export const seatsToPlay = (s: State): number[] => (s.phase === PH_ACTIVE ? [activeSeat(s)] : []);
